import { cn } from '@/lib/cn';

/**
 * Every 4:3 flag of `flag-icons` (MIT, ~270 countries and territories) as a
 * URL: each SVG is its own small file, downloaded only when a flag shows. Emoji
 * flags are no use here: Windows draws them as two letters.
 */
const FLAG_URLS = import.meta.glob<string>('/node_modules/flag-icons/flags/4x3/*.svg', {
  eager: true,
  query: '?url&no-inline',
  import: 'default',
});

const byCode = new Map(
  Object.entries(FLAG_URLS).map(([path, url]) => [path.slice(path.lastIndexOf('/') + 1, -4).toUpperCase(), url]),
);

/** The flag's URL for an ISO 3166-1 alpha-2 code ("AR"), or null when there is none. */
export function countryFlagUrl(code: string): string | null {
  return byCode.get(code.toUpperCase()) ?? null;
}

/**
 * A country's flag, 4:3, sized to sit before a line of text. Unknown codes
 * show the code itself in the same box, so a row never loses its marker.
 * Decorative: the country's name is always written next to it.
 */
export function CountryFlag({ code, className }: { code: string; className?: string }) {
  const url = countryFlagUrl(code);
  const box = 'inline-flex h-3.5 w-[1.125rem] shrink-0 overflow-hidden ring-1 ring-line';
  if (!url) {
    return (
      <span className={cn(box, 'items-center justify-center bg-surface-hover text-2xs font-semibold leading-none text-ink-2', className)} aria-hidden="true">
        {code.toUpperCase().slice(0, 2)}
      </span>
    );
  }
  return <img src={url} alt="" aria-hidden="true" loading="lazy" className={cn(box, 'object-cover', className)} />;
}
